import React, { useEffect, useState } from "react";
import {
  Heading,
  Flex,
  Avatar,
  Box,
  FormLabel,
  FormControl,
  Text,
  Divider,
  Center,
  Input,
  Button,
  Container, Spinner, useToast
} from "@chakra-ui/react";
import { Link } from "react-router-dom";

import Layout from "@pages/Layout";
import { Separator, ToggleSwitch, ProfileAttr } from "@components";
import { IconPicture, IconAvatar } from "@assets/Icons";
import {
  loadProfileData,
  editProfilePicture,
  allowNotifications,
} from "@lib/datahandlers/company";
import { loadState } from "@lib/localStorage";
import { t } from "@lang/i18n";

const toggleStyle = { margin: "auto 2rem auto 0" };

const ProfilScreen = () => {
  const [notification, setNotification] = useState({ notif_push: undefined, notif_mail: undefined });
  const [avatar, setAvatar] = useState({ picture: "", file: "" });
  const [profile, setProfile] = useState({});
  const [loading_img, setLoading_img] = useState(false);

  const toast = useToast();

  // loop through arr & dispaly it as a string
  const dispalyArrayToString = arr => {
    if (arr.length === 1) return arr.map((el) => el.name);
    else if (arr.length > 1) return arr.map((el) => el.name).join(", ");
  };
  const isObjectEmpty = obj => {
    if (Object.entries(obj).length === 0) return true;
    return false;
  };

  // handle changes in switch fields (push / mail)
  const handleChange = (e) => {
    const { name, checked } = e.target;

    setNotification({
      ...notification,
      [name]: checked ? 1 : 0
    });
    console.log(`name: ${name} / checked: ${checked}`);
  };

  // edit notification setting
  const editNotificationSettings = async () => {
    const result = await allowNotifications(notification);
    if (result.success) return;
  };

  useEffect(() => {
    if (notification.notif_mail !== undefined && notification.notif_push !== undefined ) {
      editNotificationSettings();
    }
  }, [notification]);

  // change company's picture
  const changeAvatarPicture = async (e) => {
    // set up img in state & display it
    e.preventDefault();
    setLoading_img(true);
    const file = e.target.files[0];
    try {
      const reader = new FileReader();

      reader.onloadend = () => {
        setAvatar({
          file: file,
          picture: reader.result,
        });
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.log("fileReader: ", err);
    }

    // upload img to server
    const formData = new FormData();
    formData.append("picture", file);

    try {
      const result = await editProfilePicture(formData);
      if (result.success) {
        setProfile({
          ...profile,
          picture: result.data.picture,
        });
        setLoading_img(false);
        toast({ 
          description: "La photo de profile a été ajouté/modifié",
          status: "success", position: "bottom-right", variant: "left-accent", isClosable: true 
        })
        return;
      }
    } catch (err) {
      setLoading_img(false);
      toast({ 
        description: "Une erreur s'est déroulé lors de l'ajout/modification de la photo de profile",
        status: "error", position: "bottom-right", variant: "left-accent", isClosable: true 
      })
    }
  };

  // load profile data
  const onLoad = async () => {
    try {
      const { id } = loadState("user-data");
      const result = await loadProfileData(id);
      if (result.success) {
        const { email, picture, address, company, notif_mail, notif_push, phone } = result.data;
        setProfile({
          picture: picture,
          name: company.name,
          description: company.description,
          address: address,
          activities: dispalyArrayToString(company.activities),
          fonctions: dispalyArrayToString(company.fonctions),
          email: email,
          founded_in: company.founded_in,
          size: company.size,
          siret: company.siret,
          notif_mail: notif_mail,
          notif_push: notif_push,
          phone,
          website: company.website
        });
        return;
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  useEffect(() => {
    if (isObjectEmpty(profile)) {
      onLoad();
    }
  }, []);

  useEffect(() => {
    if (profile) {
      setNotification({ notif_mail: profile.notif_mail, notif_push: profile.notif_push});
    }
  }, [profile]);
  
  return (
    <Layout menuType="company" isNavBarVisible isFooterVisible>
      <Container maxW="1024px" bg="#FFFC" minHeight='calc(100vh - 100px)' py="36px" px={["16px","","","100px"]} borderRadius="4px">
        <Flex flexDir="column">
          <Heading as="h1" fontSize="30px" textColor="black" textAlign="center">
            {t("company.profile.main.title")}
          </Heading>

          <Separator />

          <Flex flexDir="column" justifyContent="center">
            <EditPicture data={profile.picture ? profile.picture : avatar.picture} upload={changeAvatarPicture} loading={loading_img} />

            <Heading as="h2" fontSize="20px" color="green.main" m="26px auto 9px auto">
              {profile.name}
            </Heading>

            <Text fontWeight="700" fontSize="1rem" mb="15px" display="flex" justifyContent={{base: "center", md: "space-between"}}>
              {t("company.profile.main.about")}
            </Text>

            <Text fontWeight="400" fontSize="1rem" display="flex" justifyContent={{base: "center", md: "space-between"}}>
              {profile.description}
            </Text>

            <Text fontWeight="700" fontSize="1rem" mb="15px" mt="30px" display="flex" justifyContent={{base: "center", md: "space-between"}}>
              {t("company.profile.main.company_info")}
            </Text>

            <Flex flexDir="row" flexWrap="wrap" justifyContent={{base: "center", md: "space-between"}}>
              <Flex flexDir="column" w="350px" mb={["12px", "", "0px", "0px"]}>  
                <ProfileAttr FieldName={`${t("fields.company.address")} :`} data={profile.address} />
                <ProfileAttr FieldName={`${t("fields.company.activities")} :`} data={profile.activities} />
                <ProfileAttr FieldName={`${t("fields.company.fonctions")} :`} data={profile.fonctions} />
              </Flex>

              <Center height="7rem" m="auto 0 auto 0" display={["none", "", "block"]}>
                <Divider orientation="vertical" borderLeft=".5px solid #888" h="6rem" />
              </Center>

              <Flex flexDir="column" w="350px">
                <ProfileAttr FieldName={`${t("fields.email")} :`} data={profile.email} />
                <ProfileAttr FieldName={`${t("fields.company.founded_in")} :`} data={profile.founded_in} />
                <ProfileAttr FieldName={`${t("fields.company.size")} :`} data={profile.size} />
                <ProfileAttr FieldName={`${t("fields.company.siret")} :`} data={profile.siret} />
                <ProfileAttr FieldName={`${t("fields.company.phone")} :`} data={profile.phone} />
                <ProfileAttr FieldName={`${t("fields.company.web_site")} :`} data={profile.website} />
              </Flex>
            </Flex>

            <Flex flexDir="row" justifyContent={{base: "center", md: "space-between", lg: "space-evenly"}} flexWrap="wrap" mt="35px" mb="60px">
              <Link to="/company/profile/edit-profile">
                <Button w={{base: "60vw", md: "344px"}} bg="green.main" lineHeight="1.2" borderRadius="24px" p=".5rem 0" color="white" textAlign="center" 
                        m="0 1rem 24px 1rem" _hover={{ bg: "green.light" }}>
                  {t("company.profile.main.edit_profile")}
                </Button>
              </Link>

              <Link to="/company/profile/reset-password">
                <Button w={{base: "60vw", md: "344px"}} bg="green.main" lineHeight="1.2" borderRadius="24px" p=".5rem 0" color="white" textAlign="center" 
                        m="0 1rem 24px 1rem" _hover={{ bg: "green.light" }}>
                  {t("company.profile.main.edit_password")}
                </Button>
              </Link>

              {/* <Link to="/company/profile/purchase">
                <Button w={{base: "60vw", md: "344px"}} bg="green.main" lineHeight="1.2" borderRadius="24px" p=".5rem 0" color="white" textAlign="center" 
                        m="0 1rem 24px 1rem" _hover={{ bg: "green.light" }}>
                  {t("company.profile.main.purchase")}
                </Button>
              </Link> */}
            </Flex>

            <Text fontWeight="700" fontSize="1rem" mb="1rem" display="flex" justifyContent={{base: "center", md: "space-around", lg: "space-between"}}>
              {t("company.profile.main.allow_notifications")}
            </Text>

            <Flex flexDir={{base: "column", md:"row"}} justifyContent={{base: "center", md: "space-around", lg: "space-between"}} alignItems={["center", "", "", ""]}>
              <FormControl display="flex" alignItems="center" w="15rem">
                <label htmlFor="push_notification" style={toggleStyle}>
                  {t("company.profile.main.push_notif")}
                </label>
                <ToggleSwitch
                  name="notif_push"
                  id="push_notification"
                  onChange={(e) => handleChange(e)}
                  checked={notification.notif_push ? true : false}
                  type="company"
                />  
              </FormControl>

              <FormControl display="flex" alignItems="center" w="15rem">
                <label htmlFor="mail_notification" style={toggleStyle}>
                  {t("company.profile.main.email_notif")}
                </label>              
                <ToggleSwitch
                  name="notif_mail"
                  id="mail_notification"
                  onChange={(e) => handleChange(e)}
                  checked={notification.notif_mail ? true : false}
                  type="company"
                />  
              </FormControl>
            </Flex>
          </Flex>
        </Flex>
      </Container>
    </Layout>
  );
};

const EditPicture = ({ data, upload, loading }) => {
  return(
      <Box pos="relative" mx="auto">
          <Avatar src={data ? data : IconAvatar} name="avatar" size="xl" />
          <FormLabel
              m="0 !important"
              border="1px solid"
              borderColor="white"
              w="2.25rem"
              p="5px"
              borderRadius="50%"
              bg="white"
              boxShadow="2px 2px 20px rgba(0, 0, 0, 0.15)"
              pos="absolute"
              bottom="0"
              transform="translate(180%, 0)"
              _hover={{ cursor: "pointer" }}
              htmlFor="file-img-profile"
          >
              { loading ? <Spinner size="md" thickness="3px" display="flex" color="green.main" /> : <IconPicture iconType="company" /> }
              <Input type="file" id="file-img-profile" onChange={upload} display="none" />
          </FormLabel>
      </Box>
  )
};

export default ProfilScreen;
